// Permission tables + pure helpers used by AuthContext to compute the
// effective permission list for a logged-in user. Extracted so the
// permission rules are unit-testable without mounting the React context.

// ─── Role → permissions map ─────────────────────────────────────────────────
// Note: the `'fun-jumper'` permission is NOT in the static customer array.
// Self-manifest access is gated on `customerType === 'fun-jumper'` via
// `computeCustomerPermissions(customerType)` below — tandem and student
// customers should never see the Fun Jumper section.
export const ROLE_PERMISSIONS = {
  owner: ['all'],
  admin: ['dashboard', 'manifest', 'staff', 'customers', 'documents', 'check-in', 'aircraft', 'safety', 'finance', 'reports', 'admin', 'media', 'packing', 'gift-cards', 'voice-agent', 'profile'],
  manifest: ['dashboard', 'manifest', 'customers', 'documents', 'aircraft', 'media', 'packing', 'gift-cards', 'voice-agent', 'profile'],
  instructor: ['dashboard', 'schedule', 'documents', 'safety', 'media', 'profile'],
  'tandem-instructor': ['dashboard', 'schedule', 'customers', 'documents', 'safety', 'media', 'profile'],
  'aff-instructor': ['dashboard', 'schedule', 'customers', 'documents', 'safety', 'media', 'profile'],
  'wingsuit-instructor': ['dashboard', 'schedule', 'customers', 'documents', 'safety', 'media', 'profile'],
  coach: ['dashboard', 'schedule', 'customers', 'documents', 'safety', 'media', 'profile'],
  pilot: ['dashboard', 'manifest', 'aircraft', 'schedule', 'profile'],
  packer: ['dashboard', 'packing', 'profile'],
  rigger: ['dashboard', 'packing', 'safety', 'documents', 'profile'],
  // 'items' = the customer-facing dropzone shop (/app/shop). It is a
  // customer-lane module only: staff sell/fulfil items from the customer
  // profile page, so it never enters the staff nav (see `customerOnly` in
  // AppLayout's nav map).
  // 'my-gift-cards' = the customer-facing gift card page (/app/my-gift-cards):
  // buy a card (wallet or Clover) and email it to someone. Customer-lane only —
  // staff issue/manage cards via the separate 'gift-cards' module.
  customer: ['dashboard', 'bookings', 'wallet', 'items', 'my-gift-cards', 'check-in', 'waivers', 'my-media', 'profile'],
  safety: ['dashboard', 'manifest', 'safety', 'customers', 'documents', 'waivers', 'media', 'profile'],
  finance: ['dashboard', 'finance', 'reports', 'customers', 'documents', 'bookings', 'gift-cards', 'profile'],
};

export const getRolePermissions = (role) => ROLE_PERMISSIONS[role] || ['profile'];

/**
 * Merge permissions from multiple staff roles into a single deduplicated
 * array. If any role gives 'all', the result is ['all'].
 *
 * @param {string[]} roles                — staff roles the user holds
 * @param {string[]} [additionalModules]  — extra modules from
 *   StaffRoleConfig.uiVisibility.additionalModules (computed by backend
 *   at login and surfaced on the user payload). These are union-merged
 *   AFTER the base map so admin edits in the UI Visibility tab take
 *   effect on the frontend the same way the backend's hasPermission
 *   middleware already honours them.
 */
export const mergeStaffPermissions = (roles, additionalModules = []) => {
  if (!roles || roles.length === 0) return ['profile'];
  const merged = new Set();
  for (const role of roles) {
    const perms = getRolePermissions(role);
    if (perms.includes('all')) return ['all'];
    perms.forEach((p) => merged.add(p));
  }
  if (Array.isArray(additionalModules)) {
    additionalModules.forEach((m) => { if (m) merged.add(m); });
  }
  return Array.from(merged);
};

/**
 * Effective permissions for a customer-role user. Only fun-jumpers (who
 * self-manifest with their own license + rig) get the `'fun-jumper'`
 * permission that unlocks the Self-Manifest nav item. Tandem and student
 * customers never see it.
 */
export const computeCustomerPermissions = (customerType) => {
  const base = getRolePermissions('customer');
  if (customerType === 'fun-jumper') {
    return [...base, 'fun-jumper'];
  }
  return base;
};
